import { EventEmitter, Injectable, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';

import { Language } from '../data/model';
import { Constants } from '../common/constant';
import { LanguageEnum } from '../common/enum';
import { LayoutService } from './layout.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  @Output() languageChanged = new EventEmitter<Language>();
  private translationsList: any = {};

  // public fields
  currentLanguage$: BehaviorSubject<Language> = new BehaviorSubject(new Language());
  supportedLanguage$: BehaviorSubject<Language[]> = new BehaviorSubject<Language[]>([]);

  // private fields
  private supportedLanguagesList = [
    { name: 'العربية', isoCode: LanguageEnum.Arabic } as Language,
    { name: 'English', isoCode: LanguageEnum.English } as Language,
  ];

  getSupportedLanguagesList() {
    return this.supportedLanguagesList;
  }

  constructor(
    private translate: TranslateService,
    private cookieService: CookieService,
    private layoutService: LayoutService
  ) {
    this.setupAppLanguageList();
    this.initialState();
    this.supportedLanguage$.subscribe((languages) => {
      if (!languages.length) {
        return;
      }
      this.initialState();
    });
  }

  changeLanguage(languageIsoCode: LanguageEnum, hardReload: boolean) {
    if (this.currentLanguage$.value.isoCode === languageIsoCode) {
      return;
    }
    const language = this.supportedLanguagesList.find((lang) => languageIsoCode === lang.isoCode);
    if (language) {
      this.saveCurrentLanguage(language);
      this.languageChanged.emit(language);
    }
    if (hardReload) {
      location.reload()
    }
  }

  checkLanguageDirection(languageIsoCode: string): 'rtl' | 'ltr' {
    if (languageIsoCode === 'ar') {
      return 'rtl';
    } else {
      return 'ltr';
    }
  }

  setOrganizationSupportedLanguage(supportedLanguages: Language[]) {
    this.supportedLanguagesList = [];
    this.supportedLanguagesList = supportedLanguages;
    this.supportedLanguage$.next(this.supportedLanguagesList);
  }

  private getLanguageConfirmationText() {
    this.translate.get(['ChangeLanguageConfirm']).subscribe((translations: any) => {
      this.translationsList['ChangeLanguageConfirm'] = translations['ChangeLanguageConfirm'];
    });
  }

  private saveCurrentLanguage(language: Language) {
    this.currentLanguage$.next(language);
    this.cookieService.set(
      Constants.APP_LANGUAGE_COOKIE_KEY,
      this.currentLanguage$.value.isoCode,
      new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      '/'
    );

    this.translate.use(this.currentLanguage$.value.isoCode);
    this.translate.setDefaultLang(this.currentLanguage$.value.isoCode)
    this.setWebSiteDirection();
  }

  private setupAppLanguageList() {
    const languagesArray: string[] = [];
    this.supportedLanguagesList.forEach((lang) => {
      languagesArray.push(lang.isoCode);
    });
    this.translate.addLangs(languagesArray);
  }

  private setWebSiteDirection() {
    const direction = this.checkLanguageDirection(this.currentLanguage$.value.isoCode);
    document.body.dir = direction;
    document.body.parentElement!.dir = direction;
    document.body.firstElementChild!.setAttribute('dir', direction);
    this.layoutService.websiteDirection$.next(direction);
  }

  private initialState() {
    const appLangIsoCode = this.cookieService.get(Constants.APP_LANGUAGE_COOKIE_KEY);
    let currentLanguage: Language;
    if (appLangIsoCode) {
      currentLanguage = this.supportedLanguagesList.find((lang) => lang.isoCode === appLangIsoCode)!;
    } else {
      currentLanguage = this.supportedLanguagesList[0];
      this.saveCurrentLanguage(currentLanguage);
    }
    this.currentLanguage$.next(currentLanguage);


    this.translate.use(this.currentLanguage$.value.isoCode);
    this.translate.setDefaultLang(this.currentLanguage$.value.isoCode);
    this.setWebSiteDirection();  
  }
}
